import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  createAdvert,
  editAdvert,
  toggleAdvertStatus,
  deleteAdvert,
  restoreAdvert,
  fetchAllAdverts,
  fetchPositions,
  fetchAdvertStats,
  fetchAdvertsSummary,
} from "./advertThunk";

const initialState = {
  adverts: { live: [], upcoming: [], past: [], inactive: [], deleted: [] },
  positions: [],
  summary: [],
  selectedAdvertStats: null,
  listLoading: false,
  actionLoading: false,
  statsLoading: false,
};

const advertSlice = createSlice({
  name: "advert",
  initialState,
  reducers: {
    clearAdvertStats: (state) => {
      state.selectedAdvertStats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── list ──
      .addCase(fetchAllAdverts.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(fetchAllAdverts.fulfilled, (state, action) => {
        state.listLoading = false;
        state.adverts = action.payload;
      })
      .addCase(fetchAllAdverts.rejected, (state, action) => {
        state.listLoading = false;
        message.error(action.payload || "Failed to load ads");
      })

      // ── positions ──
      .addCase(fetchPositions.fulfilled, (state, action) => {
        state.positions = action.payload;
      })
      .addCase(fetchPositions.rejected, (state, action) => {
        message.error(action.payload || "Failed to load positions");
      })

      // ── summary ──
      .addCase(fetchAdvertsSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      .addCase(fetchAdvertsSummary.rejected, (state, action) => {
        message.error(action.payload || "Failed to load ad summary");
      })

      // ── stats (single ad) ──
      .addCase(fetchAdvertStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchAdvertStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.selectedAdvertStats = action.payload;
      })
      .addCase(fetchAdvertStats.rejected, (state, action) => {
        state.statsLoading = false;
        message.error(action.payload || "Failed to load ad stats");
      })

      // ── create ──
      .addCase(createAdvert.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createAdvert.fulfilled, (state, action) => {
        state.actionLoading = false;
        message.success(action.payload?.message || "Advert created");
      })
      .addCase(createAdvert.rejected, (state, action) => {
        state.actionLoading = false;
        message.error(action.payload || "Failed to create advert");
      })

      // ── edit ──
      .addCase(editAdvert.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(editAdvert.fulfilled, (state, action) => {
        state.actionLoading = false;
        message.success(action.payload?.message || "Advert updated");
      })
      .addCase(editAdvert.rejected, (state, action) => {
        state.actionLoading = false;
        message.error(action.payload || "Failed to update advert");
      })

      // ── status toggle ──
      .addCase(toggleAdvertStatus.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(toggleAdvertStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        message.success(action.payload?.message || "Status updated");
      })
      .addCase(toggleAdvertStatus.rejected, (state, action) => {
        state.actionLoading = false;
        message.error(action.payload || "Failed to update status");
      })

      // ── delete (soft) ──
      .addCase(deleteAdvert.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteAdvert.fulfilled, (state, action) => {
        state.actionLoading = false;
        message.success(action.payload?.message || "Advert deleted");
      })
      .addCase(deleteAdvert.rejected, (state, action) => {
        state.actionLoading = false;
        message.error(action.payload || "Failed to delete advert");
      })

      // ── restore ──
      .addCase(restoreAdvert.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(restoreAdvert.fulfilled, (state, action) => {
        state.actionLoading = false;
        message.success(action.payload?.message || "Advert restored");
      })
      .addCase(restoreAdvert.rejected, (state, action) => {
        state.actionLoading = false;
        message.error(action.payload || "Failed to restore advert");
      });
  },
});

export const { clearAdvertStats } = advertSlice.actions;
export default advertSlice.reducer;
